// /app/(todos)/todos/components/TodoView.tsx
'use client';

import { type TodoProject, type TodoTask } from '@prisma/client';
import { isFuture, isPast, isToday } from 'date-fns';
import { AnimatePresence, motion } from 'framer-motion';
import { Hash, PanelLeft } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogOverlay, DialogTitle } from '../../../components/ui/dialog';

import { createProject, deleteProject, updateProject } from '../lib/actions/todoActions';
import { AddTaskForm } from './AddTaskForm';
import { TaskList } from './TaskList';
import { getProjectIcon, mainFilters, TodoSidebar } from './TodoSidebar';

import { type ProjectCounts, type SidebarCounts } from './TodoSidebar';

type UserInfo = { name?: string | null; image?: string | null };

export function TodoView({
  initialTasks,
  initialProjects,
  user,
  initialOpenTaskId,
}: {
  initialTasks: TodoTask[];
  initialProjects: TodoProject[];
  user: UserInfo;
  initialOpenTaskId?: string | null;
}) {
  const [tasks, setTasks] = useState(initialTasks);
  const [projects, setProjects] = useState(initialProjects);

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [activeFilter, setActiveFilter] = useState('today');

  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);

  useEffect(() => {
    setTasks(initialTasks);
  }, [initialTasks]);

  useEffect(() => {
    setProjects(initialProjects);
  }, [initialProjects]);

  const { counts, projectCounts } = useMemo(() => {
    const counts: SidebarCounts = {
      inbox: 0,
      today: 0,
      upcoming: 0,
      overdue: 0,
      completed: 0,
    };

    const projectCounts: ProjectCounts = {};

    for (const t of tasks) {
      if (t.isCompleted) {
        counts.completed++;
        continue;
      }

      if (!t.projectId) counts.inbox++;
      else projectCounts[t.projectId] = (projectCounts[t.projectId] ?? 0) + 1;

      if (t.dueDate) {
        const d = new Date(t.dueDate);
        if (isToday(d)) counts.today++;
        else if (isFuture(d)) counts.upcoming++;
        else if (isPast(d) && !isToday(d)) counts.overdue++;
      }
    }

    return { counts, projectCounts };
  }, [tasks]);

  // ✅ Чуть быстрее, чем projects.find(...) при каждом рендере
  const projectsById = useMemo(() => {
    const map = new Map<string, TodoProject>();
    for (const p of projects) map.set(p.id, p);
    return map;
  }, [projects]);

  const currentView = useMemo(() => {
    const filter = mainFilters.find((f) => f.id === activeFilter);
    if (filter) return { title: filter.label, icon: getProjectIcon(filter.label, 24) };

    const project = projectsById.get(activeFilter);
    if (project) return { title: project.name, icon: getProjectIcon(project.name, 24) };

    return { title: 'Задачи', icon: <Hash size={28} className="text-slate-400" /> };
  }, [activeFilter, projectsById]);

  // --- Callbacks ---
  const handleTaskAdded = useCallback((newTask: TodoTask) => {
    setTasks((prev) => [...prev, newTask]);
    setIsAddModalOpen(false);
  }, []);

  const handleTaskDeleted = useCallback((taskId: string) => {
    setTasks((prev) => prev.filter((t) => t.id !== taskId));
  }, []);

  const handleTaskUpdated = useCallback((updatedTask: TodoTask) => {
    setTasks((prev) => prev.map((t) => (t.id === updatedTask.id ? updatedTask : t)));
  }, []);

  const handleProjectAdded = useCallback((newProject: TodoProject) => {
    setProjects((prev) => [...prev, newProject]);
  }, []);

  const handleCreateProjectFromName = useCallback(
    async (name: string): Promise<TodoProject | void> => {
      const newProject = await createProject({ name });
      if (newProject) {
        handleProjectAdded(newProject);
        setActiveFilter(newProject.id);
        return newProject;
      }
    },
    [handleProjectAdded],
  );

  const handleProjectUpdated = useCallback(async (id: string, name: string) => {
    const updatedProject = await updateProject(id, { name });
    if (updatedProject) {
      setProjects((prev) => prev.map((p) => (p.id === id ? updatedProject : p)));
      return updatedProject;
    }
  }, []);

  const handleProjectDeleted = useCallback(async (id: string) => {
    await deleteProject(id);

    // ✅ ВАЖНО: после того как мы сделали deleteProject() с удалением задач —
    // чистим и projects, и tasks локально.
    setProjects((prev) => prev.filter((p) => p.id !== id));
    setTasks((prev) => prev.filter((t) => t.projectId !== id));

    // ✅ Если был активен удалённый проект — переключаемся
    setActiveFilter((prev) => (prev === id ? 'inbox' : prev));
  }, []);

  const handleOpenModal = useCallback(() => setIsAddModalOpen(true), []);
  const handleCloseModal = useCallback(() => setIsAddModalOpen(false), []);

  const openMobileSidebar = useCallback(() => setIsMobileSidebarOpen(true), []);
  const closeMobileSidebar = useCallback(() => setIsMobileSidebarOpen(false), []);

  return (
    <>
      {/* ✅ Мобильный сайдбар (Drawer) */}
      <AnimatePresence>
        {isMobileSidebarOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 bg-black/60 z-30 lg:hidden"
              onClick={closeMobileSidebar}
            />
            <motion.div
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ type: 'spring', stiffness: 300, damping: 30 }}
              className="fixed top-0 left-0 bottom-0 w-72 bg-slate-900 z-40 lg:hidden"
            >
              <TodoSidebar
                isMobile
                user={user}
                projects={projects}
                counts={counts}
                projectCounts={projectCounts}
                activeFilter={activeFilter}
                setActiveFilter={setActiveFilter}
                onAddTaskClick={handleOpenModal}
                onProjectCreated={handleCreateProjectFromName}
                onProjectUpdated={handleProjectUpdated}
                onProjectDeleted={handleProjectDeleted}
                onClose={() => setIsMobileSidebarOpen(false)}
              />
            </motion.div>
          </>
        )}
      </AnimatePresence>

      <div
        className="flex min-h-[calc(100dvh-80px)] text-white gap-6"
        role="region"
        aria-label="Интерфейс списка задач"
      >
        {/* Десктопный сайдбар */}
        <div className="hidden lg:block lg:w-72 flex-shrink-0">
          <TodoSidebar
            user={user}
            projects={projects}
            counts={counts}
            projectCounts={projectCounts}
            activeFilter={activeFilter}
            setActiveFilter={setActiveFilter}
            onAddTaskClick={handleOpenModal}
            onProjectCreated={handleCreateProjectFromName}
            onProjectUpdated={handleProjectUpdated}
            onProjectDeleted={handleProjectDeleted}
          />
        </div>

        <main className="flex-1 flex flex-col p-4 md:p-0 overflow-hidden">
          <div className="flex items-center gap-3 mb-4 md:mb-6">
            <button
              onClick={openMobileSidebar}
              className="lg:hidden p-1 text-slate-400 hover:text-white"
              aria-label="Открыть меню"
            >
              <PanelLeft size={24} />
            </button>

            <span className="text-2xl md:text-4xl">{currentView.icon}</span>
            <h2 id="task-list-title" className="text-2xl md:text-3xl font-bold text-main truncate">
              {currentView.title}
            </h2>
          </div>

          <div className="flex-1 min-h-0 overflow-hidden pr-2 md:pr-4">
            <TaskList
              tasks={tasks}
              activeFilter={activeFilter}
              onTaskDeleted={handleTaskDeleted}
              onTaskUpdated={handleTaskUpdated}
              viewTitle={currentView.title}
              initialOpenTaskId={initialOpenTaskId}
            />
          </div>
        </main>
      </div>

      <Dialog open={isAddModalOpen} onOpenChange={setIsAddModalOpen}>
        <DialogOverlay className="bg-black/60 backdrop-blur-sm" />
        <DialogContent className="bg-slate-800 border-slate-700 text-white sm:max-w-[625px] overflow-visible p-2">
          <DialogTitle className="sr-only">Добавить новую задачу</DialogTitle>
          <AddTaskForm
            projects={projects}
            onTaskAdded={handleTaskAdded}
            onProjectCreated={handleProjectAdded}
            onCancel={handleCloseModal}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}
